import type { AggregateItem, HotItem, Hotlist } from "../types";

function normalizeTitle(title: string): string {
  return title
    .normalize("NFKC")
    .toLocaleLowerCase("zh-CN")
    .replace(/[\s\p{P}\p{S}]+/gu, "")
    .replace(/^(突发|最新|热搜|视频)/, "");
}

function bigrams(value: string): Set<string> {
  const result = new Set<string>();
  for (let index = 0; index < value.length - 1; index += 1) {
    result.add(value.slice(index, index + 2));
  }
  return result;
}

function isSimilar(left: string, right: string): boolean {
  if (left === right) return true;
  if (left.length >= 8 && right.length >= 8 && (left.includes(right) || right.includes(left))) return true;
  if (Math.min(left.length, right.length) < 6) return false;

  const leftPairs = bigrams(left);
  const rightPairs = bigrams(right);
  let intersection = 0;
  for (const pair of leftPairs) if (rightPairs.has(pair)) intersection += 1;
  return intersection / Math.max(leftPairs.size, rightPairs.size) >= 0.68;
}

function aggregateId(normalizedTitle: string): string {
  let hash = 2166136261;
  for (let index = 0; index < normalizedTitle.length; index += 1) {
    hash ^= normalizedTitle.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `aggregate-${(hash >>> 0).toString(36)}`;
}

interface Group {
  normalizedTitle: string;
  items: HotItem[];
}

export function buildAggregate(hotlists: Hotlist[]): AggregateItem[] {
  const groups: Group[] = [];

  for (const hotlist of hotlists) {
    for (const item of hotlist.items) {
      const normalizedTitle = normalizeTitle(item.title);
      if (!normalizedTitle) continue;
      const group = groups.find((candidate) => isSimilar(candidate.normalizedTitle, normalizedTitle));
      if (group && !group.items.some((existing) => existing.platformId === item.platformId)) {
        group.items.push(item);
      } else if (!group) {
        groups.push({ normalizedTitle, items: [item] });
      }
    }
  }

  return groups
    .map((group) => {
      const sorted = [...group.items].sort((left, right) => left.rank - right.rank);
      const lead = sorted[0];
      if (!lead) return null;
      const sourceCount = sorted.length;
      return {
        id: aggregateId(group.normalizedTitle),
        title: lead.title,
        url: lead.url,
        score: sorted.reduce((total, item) => total + Math.max(1, 12 - item.rank), 0) + sourceCount * 8,
        sourceCount,
        sources: sorted.map((item) => ({ platformId: item.platformId, rank: item.rank, url: item.url })),
        updatedAt: sorted.map((item) => item.fetchedAt).sort().at(-1) || new Date(0).toISOString(),
      } satisfies AggregateItem;
    })
    .filter((item): item is AggregateItem => item !== null)
    .sort((left, right) => right.sourceCount - left.sourceCount || right.score - left.score)
    .slice(0, 8);
}
